import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';

import { isEqual } from 'lodash';
import { IoClose } from 'react-icons/io5';
import { RiCheckLine } from 'react-icons/ri';
import { Currency, Input } from 'ui/components/form';
import c, { ProductType } from 'utils/constants';
import { cn } from 'utils/helpers';
import { Can } from 'utils/providers/RolePermission';
import { Product } from 'utils/types';

interface PROPS {
  type: ProductType;
  defaultValue?: Product;
  onClickClose: () => void;
  onClickSubmit: (product: Omit<Product, '_id' | 'type'>) => void;
  isLoading: boolean;
  disabled?: boolean;
}

export interface REF {
  onClearForm: () => void;
}

const ProductInput = forwardRef<REF, PROPS>(
  (
    { type, defaultValue, onClickClose, onClickSubmit, isLoading, disabled },
    ref,
  ) => {
    const { name, price, description } = defaultValue ?? {
      name: '',
      price: undefined,
      description: '',
    };

    const [itemName, setItemName] = useState(name);
    const [itemPrice, setItemPrice] = useState<number | undefined>(price);
    const [itemDescription, setItemDescription] = useState(description);
    const { ACTIONS, PERMISSION_KEYS } = c;

    const amountPlaceHolder = useMemo(
      () => (type === ProductType.Quantity ? 'Amount' : 'Rate'),
      [type],
    );

    const handleClickSubmit = () => {
      onClickSubmit({
        name: itemName ?? '',
        price: !itemPrice ? 0 : +itemPrice,
        description: itemDescription,
      });
    };

    useImperativeHandle(ref, () => ({
      onClearForm: () => {
        setItemName('');
        setItemPrice(undefined);
        setItemDescription('');
      },
    }));

    const isChanged = useMemo(
      () =>
        !isEqual(
          { name, price, description },
          {
            name: itemName,
            price: itemPrice,
            description: itemDescription,
          },
        ),
      [description, itemDescription, itemName, itemPrice, name, price],
    );

    useEffect(() => {
      setItemName(name);
      setItemPrice(price);
      setItemDescription(description);
    }, [description, name, price]);

    return (
      <>
        <Input
          disabled={disabled}
          placeholder="Item Name"
          className="col-span-4 w-full"
          value={itemName}
          onChange={(e) => setItemName(e.target.value)}
        />
        <Currency
          disabled={disabled}
          placeholder={amountPlaceHolder}
          className="col-span-2 w-full"
          value={itemPrice}
          onValueChange={(val) => {
            if (!val) {
              return;
            }

            setItemPrice(+val);
          }}
          min={0}
        />
        <Input
          disabled={disabled}
          placeholder="Description"
          className="col-span-5 w-full"
          value={itemDescription}
          onChange={(e) => setItemDescription(e.target.value)}
        />
        <div className="col-span-1 mr-1 flex items-center gap-x-2">
          <button
            disabled={!(itemName && itemPrice && isChanged) || isLoading}
            onClick={handleClickSubmit}
            type="button"
            className={cn(
              'text-primary-base disabled:text-disabled',
              'bg-fill-primary disabled:bg-transparent',
              'h-9 w-9 rounded-[6px]',
              'flex items-center justify-center',
            )}
          >
            <RiCheckLine className="h-5 w-5" />
          </button>
          <Can I={ACTIONS.DELETE} this={PERMISSION_KEYS.PRODUCTS}>
            <button
              onClick={onClickClose}
              type="button"
              className={cn(
                'h-9 w-9 rounded-[6px]',
                'bg-fill-primary',
                'flex items-center justify-center',
              )}
              disabled={disabled}
            >
              <IoClose className="h-5 w-5 text-content hover:text-danger-base transition" />
            </button>
          </Can>
        </div>
      </>
    );
  },
);

export default ProductInput;
